import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Eye, Plus, Trash2, ExternalLink, Save, Layers } from "lucide-react";
import { Link } from "react-router-dom";

function Section({ title, children }) {
    return (
        <div className="border border-border bg-white p-4 sm:p-5 space-y-3">
            <h3 className="font-display font-bold text-sm uppercase tracking-widest text-foreground/80">{title}</h3>
            {children}
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">{label}</label>
            {children}
            {hint && <div className="text-[10px] text-foreground/50 mt-1">{hint}</div>}
        </div>
    );
}

export default function CompanyLandingEditor() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data: res } = await api.get("/company/landing");
            setData(res);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    const set = (k, v) => setData({ ...data, landing: { ...data.landing, [k]: v } });

    const save = async () => {
        setSaving(true);
        try {
            const { data: res } = await api.patch("/company/landing", data.landing);
            setData({ ...data, landing: res.landing });
            toast.success("Landing page saved");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const addFeature = () => set("features", [...(data.landing.features || []), { title: "New feature", body: "" }]);
    const updateFeature = (i, k, v) => {
        const arr = [...(data.landing.features || [])]; arr[i] = { ...arr[i], [k]: v }; set("features", arr);
    };
    const removeFeature = (i) => {
        const arr = [...(data.landing.features || [])]; arr.splice(i, 1); set("features", arr);
    };

    const addTestimonial = () => set("testimonials", [...(data.landing.testimonials || []), { author: "", role: "", quote: "" }]);
    const updateTestimonial = (i, k, v) => {
        const arr = [...(data.landing.testimonials || [])]; arr[i] = { ...arr[i], [k]: v }; set("testimonials", arr);
    };
    const removeTestimonial = (i) => {
        const arr = [...(data.landing.testimonials || [])]; arr.splice(i, 1); set("testimonials", arr);
    };

    const addStat = () => set("stats", [...(data.landing.stats || []), { value: "", label: "" }]);
    const updateStat = (i, k, v) => {
        const arr = [...(data.landing.stats || [])]; arr[i] = { ...arr[i], [k]: v }; set("stats", arr);
    };
    const removeStat = (i) => {
        const arr = [...(data.landing.stats || [])]; arr.splice(i, 1); set("stats", arr);
    };

    if (!data) {
        return <div className="max-w-[1600px] mx-auto px-4 py-5 text-sm font-mono text-foreground/60">Loading landing editor…</div>;
    }

    const l = data.landing || {};
    const publicUrl = data.slug ? `${window.location.origin}/u/${data.slug}` : null;

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Public site</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 flex items-center gap-3">
                        <Layers className="w-8 h-8 text-ikb" /> Landing page
                    </h1>
                    <p className="text-foreground/80 mt-2 text-sm">
                        Customize your public careers page at{" "}
                        {publicUrl ? (
                            <Link to={`/u/${data.slug}`} target="_blank" className="text-ikb font-mono inline-flex items-center gap-1 hover:underline" data-testid="landing-public-link">
                                {publicUrl} <ExternalLink className="w-3 h-3" />
                            </Link>
                        ) : <span className="text-foreground/40">/u/&lt;your-slug&gt;</span>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-3 py-2 inline-flex items-center gap-2 cursor-pointer" data-testid="landing-enabled-toggle">
                        <input type="checkbox" checked={!!l.enabled} onChange={(e) => set("enabled", e.target.checked)} />
                        {l.enabled ? "Page is live" : "Page is OFF"}
                    </label>
                    <Button onClick={save} disabled={saving} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10" data-testid="landing-save">
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </div>

            <Section title="Hero">
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Eyebrow text"><Input value={l.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} className="rounded-none mt-1" data-testid="landing-hero-eyebrow" /></Field>
                    <Field label="CTA label"><Input value={l.hero_cta_label || ""} onChange={(e) => set("hero_cta_label", e.target.value)} className="rounded-none mt-1" data-testid="landing-hero-cta" /></Field>
                </div>
                <Field label="Title (H1)"><Input value={l.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className="rounded-none mt-1" data-testid="landing-hero-title" /></Field>
                <Field label="Subtitle"><Textarea value={l.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className="rounded-none mt-1 min-h-[60px]" data-testid="landing-hero-subtitle" /></Field>
                <Field label="Hero image URL" hint="Optional. Recommended 1600×900."><Input value={l.hero_image_url || ""} onChange={(e) => set("hero_image_url", e.target.value)} className="rounded-none mt-1" data-testid="landing-hero-image" placeholder="https://…" /></Field>
            </Section>

            <Section title="About">
                <Field label="Section title"><Input value={l.about_title || ""} onChange={(e) => set("about_title", e.target.value)} className="rounded-none mt-1" data-testid="landing-about-title" /></Field>
                <Field label="Body"><Textarea value={l.about_body || ""} onChange={(e) => set("about_body", e.target.value)} className="rounded-none mt-1 min-h-[100px]" data-testid="landing-about-body" /></Field>
            </Section>

            <Section title="Stats">
                <div className="space-y-2">
                    {(l.stats || []).map((s, i) => (
                        <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-end" data-testid={`landing-stat-${i}`}>
                            <Field label="Value"><Input value={s.value || ""} onChange={(e) => updateStat(i, "value", e.target.value)} className="rounded-none mt-1" placeholder="250+" /></Field>
                            <Field label="Label"><Input value={s.label || ""} onChange={(e) => updateStat(i, "label", e.target.value)} className="rounded-none mt-1" placeholder="Employees" /></Field>
                            <Button variant="outline" onClick={() => removeStat(i)} className="rounded-none text-rose-700 border-rose-300 h-9" data-testid={`landing-stat-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                    ))}
                    <Button onClick={addStat} variant="outline" className="rounded-none" data-testid="landing-stat-add"><Plus className="w-3 h-3 mr-1" /> Add stat</Button>
                </div>
            </Section>

            <Section title="Why join us · Features">
                <div className="space-y-3">
                    {(l.features || []).map((f, i) => (
                        <div key={i} className="border border-border p-3 space-y-2" data-testid={`landing-feature-${i}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Feature #{i + 1}</span>
                                <Button variant="outline" onClick={() => removeFeature(i)} className="rounded-none text-rose-700 border-rose-300 h-7 text-xs" data-testid={`landing-feature-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                            <Input value={f.title || ""} onChange={(e) => updateFeature(i, "title", e.target.value)} className="rounded-none" placeholder="Title" />
                            <Textarea value={f.body || ""} onChange={(e) => updateFeature(i, "body", e.target.value)} className="rounded-none min-h-[60px]" placeholder="Body…" />
                        </div>
                    ))}
                    <Button onClick={addFeature} variant="outline" className="rounded-none" data-testid="landing-feature-add"><Plus className="w-3 h-3 mr-1" /> Add feature</Button>
                </div>
            </Section>

            <Section title="Testimonials">
                <div className="space-y-3">
                    {(l.testimonials || []).map((t, i) => (
                        <div key={i} className="border border-border p-3 space-y-2" data-testid={`landing-testimonial-${i}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Testimonial #{i + 1}</span>
                                <Button variant="outline" onClick={() => removeTestimonial(i)} className="rounded-none text-rose-700 border-rose-300 h-7 text-xs" data-testid={`landing-testimonial-remove-${i}`}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                            <Textarea value={t.quote || ""} onChange={(e) => updateTestimonial(i, "quote", e.target.value)} className="rounded-none min-h-[60px]" placeholder="“This place changed how I think about my career…”" />
                            <div className="grid grid-cols-2 gap-2">
                                <Input value={t.author || ""} onChange={(e) => updateTestimonial(i, "author", e.target.value)} className="rounded-none" placeholder="Author" />
                                <Input value={t.role || ""} onChange={(e) => updateTestimonial(i, "role", e.target.value)} className="rounded-none" placeholder="Role · Team" />
                            </div>
                        </div>
                    ))}
                    <Button onClick={addTestimonial} variant="outline" className="rounded-none" data-testid="landing-testimonial-add"><Plus className="w-3 h-3 mr-1" /> Add testimonial</Button>
                </div>
            </Section>

            <Section title="Talent pool CTA">
                <label className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!l.show_talent_pool_cta} onChange={(e) => set("show_talent_pool_cta", e.target.checked)} data-testid="landing-pool-cta-toggle" />
                    Show "Don't see a fit?" section
                </label>
                <Field label="Title"><Input value={l.talent_pool_cta_title || ""} onChange={(e) => set("talent_pool_cta_title", e.target.value)} className="rounded-none mt-1" /></Field>
                <Field label="Body"><Textarea value={l.talent_pool_cta_body || ""} onChange={(e) => set("talent_pool_cta_body", e.target.value)} className="rounded-none mt-1 min-h-[60px]" /></Field>
            </Section>

            <Section title="Footer & socials">
                <Field label="Footer text"><Input value={l.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} className="rounded-none mt-1" /></Field>
                <div className="grid sm:grid-cols-2 gap-3">
                    {["linkedin", "twitter", "instagram", "youtube"].map((k) => (
                        <Field key={k} label={k.toUpperCase()}>
                            <Input
                                value={(l.social_links || {})[k] || ""}
                                onChange={(e) => set("social_links", { ...(l.social_links || {}), [k]: e.target.value })}
                                className="rounded-none mt-1" placeholder={`https://${k}.com/your-handle`}
                                data-testid={`landing-social-${k}`}
                            />
                        </Field>
                    ))}
                </div>
                <label className="text-xs font-mono uppercase tracking-widest inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!l.show_jobs_section} onChange={(e) => set("show_jobs_section", e.target.checked)} data-testid="landing-jobs-toggle" />
                    Show open roles section
                </label>
            </Section>

            <div className="sticky bottom-4 flex justify-end">
                <Button onClick={save} disabled={saving} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11 shadow-lg" data-testid="landing-save-bottom">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save all changes"}
                </Button>
            </div>
        </div>
    );
}
